import React, { useState, useRef, useEffect, useMemo } from 'react';
import PortalIcon from './PortalIcon';
import './OwnerPicker.css';

function initials(name) {
  return (name || '?').split(' ').filter(Boolean).map((p) => p[0]).join('').slice(0, 2).toUpperCase();
}


// Pick an owner from the accounts on this tenant, or type a name for someone
// without one (an external auditor, a manager who never logs in). `value` is
// `{ name, email }` — an empty email means "just a name".
export default function OwnerPicker({ value, onChange, members, disabled }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    function onDown(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const q = query.trim().toLowerCase();
  const matches = useMemo(
    () => (members || []).filter((m) => !q || m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q)),
    [members, q]
  );
  const exact = (members || []).some((m) => m.name.toLowerCase() === q);

  function pick(next) {
    onChange(next);
    setQuery('');
    setOpen(false);
  }

  if (value?.name) {
    const hasAccount = !!value.email;
    const title = (members || []).find((m) => m.email === value.email)?.jobTitle;
    return (
      <div className="op-chip">
        <span className={`op-av op-av--sm${hasAccount ? '' : ' op-av--ext'}`}>{initials(value.name)}</span>
        <span className="op-chip__name">{value.name}</span>
        <small>{hasAccount ? title || '' : 'Uden konto'}</small>
        {!disabled && (
          <button type="button" className="op-x" aria-label="Fjern ejer" onClick={() => onChange({ name: '', email: '' })}>
            <PortalIcon name="x" size={14} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="op" ref={rootRef}>
      <div className={`op-field${open ? ' is-focus' : ''}`}>
        <PortalIcon name="search" size={16} />
        <input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setOpen(false);
            if (e.key === 'Enter') {
              e.preventDefault();
              if (matches.length === 1) pick({ name: matches[0].name, email: matches[0].email });
              else if (q && !exact) pick({ name: query.trim(), email: '' });
            }
          }}
          placeholder="Ejer"
          aria-label="Ejer"
          disabled={disabled}
          autoComplete="off"
        />
      </div>
      {open && (matches.length > 0 || q) && (
        <div className="op-pop">
          {matches.length > 0 && <div className="op-grp">Brugere i NordComply</div>}
          {matches.map((m) => (
            <button type="button" key={m.email} className="op-opt" onClick={() => pick({ name: m.name, email: m.email })}>
              <span className="op-av">{initials(m.name)}</span>
              <span className="op-nm">{m.name}<small>{m.email}</small></span>
              {m.jobTitle && <span className="op-rl">{m.jobTitle}</span>}
            </button>
          ))}
          {q && !exact && (
            <>
              {matches.length > 0 && <div className="op-sep" />}
              <button type="button" className="op-opt op-opt--free" onClick={() => pick({ name: query.trim(), email: '' })}>
                <span className="op-plus"><PortalIcon name="plus" size={14} /></span>
                <span className="op-nm">Brug <b>"{query.trim()}"</b> som navn<small>Person uden konto, fx en ekstern</small></span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
